/**
 * Feature Request Form Component
 * Form for requesting new features or improvements
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, FeatureRequestFeedback } from '../../../types/feedback';
import ScreenshotButton from '../ScreenshotButton';

interface FeatureRequestFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<FeatureRequestFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
  enableScreenshot?: boolean;
}

const FeatureRequestForm: React.FC<FeatureRequestFormProps> = ({
  data,
  onChange,
  validation,
  currentStep,
  enableScreenshot = true
}) => {
  const featureData = data as Partial<FeatureRequestFeedback>;
  const [screenshot, setScreenshot] = useState<any>(null);

  const priorities = [
    {
      key: 'nice-to-have',
      label: 'Nice to Have',
      description: 'Would be helpful but not essential',
      color: 'text-sage-600',
      bgColor: 'bg-sage-50 border-sage-300'
    },
    {
      key: 'important',
      label: 'Important',
      description: 'Would significantly improve the experience',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 border-amber-300'
    },
    {
      key: 'critical',
      label: 'Critical',
      description: 'Essential for effective humanitarian response',
      color: 'text-coral-600',
      bgColor: 'bg-coral-50 border-coral-300'
    }
  ];

  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-serif text-slate-800 mb-2">
            Request a Feature
          </h3>
          <p className="text-sm text-slate-600">
            Help us improve CLEAR by suggesting new features or enhancements
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="feature-title" className="block text-sm font-medium text-slate-700 mb-2">
              Feature Title *
            </label>
            <input
              id="feature-title"
              type="text"
              value={featureData.title || ''}
              onChange={(e) => onChange({ ...featureData, title: e.target.value, type: 'feature-request' })}
              placeholder="Brief name for the requested feature"
              className={cn(
                'input-minimal',
                validation.errors.title && 'border-coral-300 focus:border-coral-400'
              )}
            />
            {validation.errors.title && (
              <p className="text-sm text-coral-600 mt-1">{validation.errors.title}</p>
            )}
          </div>

          <div>
            <label htmlFor="feature-description" className="block text-sm font-medium text-slate-700 mb-2">
              Feature Description *
            </label>
            <textarea
              id="feature-description"
              rows={4}
              value={featureData.description || ''}
              onChange={(e) => onChange({ ...featureData, description: e.target.value })}
              placeholder="Detailed description of the feature you'd like to see"
              className={cn(
                'input-minimal resize-none',
                validation.errors.description && 'border-coral-300 focus:border-coral-400'
              )}
            />
            {validation.errors.description && (
              <p className="text-sm text-coral-600 mt-1">{validation.errors.description}</p>
            )}
          </div>

          <div>
            <label htmlFor="use-case" className="block text-sm font-medium text-slate-700 mb-2">
              Use Case *
            </label>
            <textarea
              id="use-case"
              rows={3}
              value={featureData.useCase || ''}
              onChange={(e) => onChange({ ...featureData, useCase: e.target.value })}
              placeholder="Explain how this feature would help with humanitarian work. What problem does it solve?"
              className="input-minimal resize-none"
            />
          </div>

          {/* Screenshot Button */}
          {enableScreenshot && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">
                Add a mockup or reference image (optional)
              </label>
              <ScreenshotButton
                onScreenshotCapture={(screenshotData) => {
                  setScreenshot(screenshotData);
                  onChange({
                    ...featureData,
                    attachments: screenshotData.thumbnail ? [{
                      type: 'screenshot',
                      url: screenshotData.thumbnail,
                      metadata: screenshotData.metadata
                    }] : []
                  });
                }}
                existingScreenshot={screenshot?.thumbnail}
              />
              <p className="text-xs text-slate-500">
                You can capture a screenshot to show what you'd like to see
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          Additional Details
        </h3>
        <p className="text-sm text-slate-600">
          Help us prioritize and understand your request
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Priority Level
          </label>
          <div className="space-y-3">
            {priorities.map((priority) => (
              <button
                key={priority.key}
                onClick={() => onChange({ ...featureData, priority: priority.key as FeatureRequestFeedback['priority'] })}
                className={cn(
                  'w-full p-4 border-2 rounded-lg text-left transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2',
                  featureData.priority === priority.key
                    ? priority.bgColor + ' border-current'
                    : 'border-stone-200 hover:border-stone-300'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn('w-3 h-3 rounded-full mt-1.5',
                    priority.key === 'nice-to-have' && 'bg-sage-400',
                    priority.key === 'important' && 'bg-amber-400',
                    priority.key === 'critical' && 'bg-coral-400'
                  )} />
                  <div>
                    <div className={cn('font-medium mb-1', priority.color)}>
                      {priority.label}
                    </div>
                    <p className="text-sm text-slate-600 leading-snug">
                      {priority.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="similar-features" className="block text-sm font-medium text-slate-700 mb-2">
            Similar Features (Optional)
          </label>
          <textarea
            id="similar-features"
            rows={2}
            value={featureData.similarFeatures || ''}
            onChange={(e) => onChange({ ...featureData, similarFeatures: e.target.value })}
            placeholder="Are you aware of similar features in other tools that work well?"
            className="input-minimal resize-none"
          />
          <p className="text-xs text-slate-500 mt-1">
            Examples from other humanitarian platforms or tools can help guide development
          </p>
        </div>

        <div className="bg-stone-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-slate-800 mb-2">Review your request</h4>
          <div className="space-y-2 text-sm">
            <p><span className="font-medium">Title:</span> {featureData.title}</p>
            <p><span className="font-medium">Priority:</span> {priorities.find(p => p.key === featureData.priority)?.label}</p>
            <div>
              <span className="font-medium">Description:</span>
              <p className="mt-1 text-slate-600 italic line-clamp-3">
                "{featureData.description}"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureRequestForm;